/**
 * Page visibility, as the upload needs it.
 *
 * A phone that backgrounds the tab (switching apps, locking the screen, leaving
 * the Facebook or Instagram app) freezes or kills our timers and network. Time
 * spent like that is not the upload being slow, and a file that fails while
 * the page is hidden failed because it was hidden. These helpers let the
 * upload tell the two apart.
 */

type VisibilityDoc = Pick<
  Document,
  "visibilityState" | "addEventListener" | "removeEventListener"
>;

const browserDoc = (): VisibilityDoc | null =>
  typeof document === "undefined" ? null : document;

export function isPageHidden(doc: VisibilityDoc | null = browserDoc()): boolean {
  return doc?.visibilityState === "hidden";
}

export interface HiddenTimeTracker {
  /** Total ms the page has spent hidden since tracking began, including now. */
  hiddenMs(): number;
  dispose(): void;
}

export function createHiddenTimeTracker(
  doc: VisibilityDoc | null = browserDoc(),
  now: () => number = Date.now,
): HiddenTimeTracker {
  if (!doc) return { hiddenMs: () => 0, dispose: () => {} };
  let total = 0;
  let hiddenSince: number | null = doc.visibilityState === "hidden" ? now() : null;
  const onChange = () => {
    if (doc.visibilityState === "hidden") {
      if (hiddenSince === null) hiddenSince = now();
    } else if (hiddenSince !== null) {
      total += now() - hiddenSince;
      hiddenSince = null;
    }
  };
  doc.addEventListener("visibilitychange", onChange);
  return {
    hiddenMs: () => total + (hiddenSince !== null ? now() - hiddenSince : 0),
    dispose: () => doc.removeEventListener("visibilitychange", onChange),
  };
}

let shared: HiddenTimeTracker | null = null;

/**
 * One tracker for the page's lifetime. Lazily created so nothing listens on
 * the server or before an upload first asks.
 */
export function sharedHiddenTime(): HiddenTimeTracker {
  if (!shared) shared = createHiddenTimeTracker();
  return shared;
}

/** Resolves at once if visible, otherwise the next time the page is shown. */
export function whenVisible(doc: VisibilityDoc | null = browserDoc()): Promise<void> {
  if (!doc || doc.visibilityState !== "hidden") return Promise.resolve();
  return new Promise<void>((resolve) => {
    const onChange = () => {
      if (doc.visibilityState === "hidden") return;
      doc.removeEventListener("visibilitychange", onChange);
      resolve();
    };
    doc.addEventListener("visibilitychange", onChange);
  });
}
